<template>
  <div class="wa" :class="[`wa--${kind}`, { 'wa--gone': expired }]" :data-test="`wa-att-${kind}`">
    <!-- Expired in WhatsApp: the document existed, we simply cannot open it. -->
    <div v-if="expired" class="wa-row">
      <PaperClipIcon class="wa-ic" aria-hidden="true" />
      <span class="wa-name">{{ name }}</span>
      <span class="wa-status">media expired in WhatsApp</span>
    </div>

    <!-- No key (older feed rows): the name only. -->
    <div v-else-if="!fileKey" class="wa-row">
      <PaperClipIcon class="wa-ic" aria-hidden="true" />
      <span class="wa-name">{{ name }}</span>
    </div>

    <template v-else>
      <!-- Image: a thumbnail that opens full-size. -->
      <button
        v-if="kind === 'image'"
        type="button"
        class="wa-thumb"
        :title="`Open ${name}`"
        data-test="wa-open"
        @click="open"
      >
        <img v-if="previewUrl" :src="previewUrl" :alt="name" class="wa-thumb-img" loading="lazy" />
        <span v-else class="wa-thumb-ph" aria-hidden="true">
          <PhotoIcon class="wa-ic-lg" />
        </span>
      </button>

      <div class="wa-row">
        <component
          :is="kind === 'pdf' ? DocumentTextIcon : PaperClipIcon"
          class="wa-ic"
          aria-hidden="true"
        />
        <span class="wa-name">{{ name }}</span>
        <button
          v-if="kind !== 'image'"
          type="button"
          class="wa-link"
          data-test="wa-open"
          :disabled="busy"
          @click="open"
        >
          {{ kind === 'pdf' ? 'Preview' : 'Open' }}
        </button>
        <button
          type="button"
          class="wa-link"
          data-test="wa-download"
          :disabled="busy"
          @click="download"
        >
          Download
        </button>
      </div>
      <p v-if="failed" class="wa-status" data-test="wa-failed">The file could not be opened.</p>
    </template>

    <!-- Viewer: an image full-size, or a PDF in the browser's own viewer. -->
    <Teleport to="body">
      <div v-if="viewerUrl" class="wa-viewer" data-test="wa-viewer" @keydown.esc="viewerUrl = null">
        <div class="wa-viewer-scrim" @click="viewerUrl = null"></div>
        <div class="wa-viewer-box" role="dialog" aria-modal="true" :aria-label="name">
          <div class="wa-viewer-head">
            <span class="wa-viewer-title">{{ name }}</span>
            <button type="button" class="wa-link" @click="download">Download</button>
            <button
              type="button"
              class="dash-icon-btn"
              aria-label="Close"
              title="Close"
              @click="viewerUrl = null"
            >
              <XMarkIcon class="size-5" />
            </button>
          </div>
          <img v-if="kind === 'image'" :src="viewerUrl" :alt="name" class="wa-viewer-img" />
          <iframe v-else :src="viewerUrl" class="wa-viewer-frame" :title="name"></iframe>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
/*
  AB#21841 — one attachment from the WhatsApp feed. The file lives in the
  feed's own bucket; the API hands out a 15-minute pre-signed link per file,
  only for a key the feed tied to this order. Images show a thumbnail as soon
  as the card is on screen; a PDF fetches its link when the person asks.
*/
import { computed, onMounted, ref } from 'vue';
import { DocumentTextIcon, PaperClipIcon, PhotoIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { attachmentKind, fileLink } from '@/composables/useWhatsAppThread';

const props = defineProps<{
  orderItemId: number;
  name: string;
  fileKey?: string;
  status?: string;
}>();

const kind = computed(() => attachmentKind(props.name));
const expired = computed(() => props.status === 'expired');
const previewUrl = ref<string | null>(null);
const viewerUrl = ref<string | null>(null);
const busy = ref(false);
const failed = ref(false);

const link = async (download = false): Promise<string | null> => {
  if (!props.fileKey) return null;
  busy.value = true;
  failed.value = false;
  try {
    const url = await fileLink(props.orderItemId, props.fileKey, download);
    if (!url) failed.value = true;
    return url;
  } finally {
    busy.value = false;
  }
};

const open = async (): Promise<void> => {
  const url = previewUrl.value || (await link());
  if (url) viewerUrl.value = url;
};

const download = async (): Promise<void> => {
  const url = await link(true);
  if (url) window.open(url, '_blank', 'noopener');
};

onMounted(async () => {
  if (kind.value === 'image' && props.fileKey && !expired.value) {
    previewUrl.value = await link();
  }
});
</script>

<style scoped>
.wa {
  margin-top: 6px;
  font-size: 12.5px;
}
.wa-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 6px 8px;
  border-radius: 8px;
  background: var(--surface);
  border: 1px dashed var(--border);
}
.wa--gone {
  color: var(--ink-mute);
}
.wa-ic {
  width: 14px;
  height: 14px;
  flex: none;
}
.wa-ic-lg {
  width: 28px;
  height: 28px;
  color: var(--ink-mute);
}
.wa-name {
  overflow-wrap: anywhere;
  flex: 1;
}
.wa-status {
  font-style: italic;
  color: var(--ink-mute);
  margin: 4px 0 0;
}
.wa-link {
  border: 0;
  background: none;
  color: var(--accent);
  cursor: pointer;
  font-size: 12.5px;
  padding: 0 2px;
}
.wa-link:disabled {
  opacity: 0.5;
  cursor: default;
}
.wa-thumb {
  display: block;
  width: min(100%, 260px);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  overflow: hidden;
  background: var(--surface-3);
  padding: 0;
  margin-bottom: 6px;
  cursor: zoom-in;
}
.wa-thumb-img {
  display: block;
  width: 100%;
  max-height: 220px;
  object-fit: cover;
}
.wa-thumb-ph {
  display: grid;
  place-items: center;
  height: 90px;
}
.wa-viewer-scrim {
  position: fixed;
  inset: 0;
  top: var(--app-bar-offset, 53px);
  background: rgba(10, 12, 16, 0.7);
  z-index: 10050;
}
.wa-viewer-box {
  position: fixed;
  left: 50%;
  top: calc(var(--app-bar-offset, 53px) + 24px);
  transform: translateX(-50%);
  width: min(96vw, 1000px);
  height: calc(100vh - var(--app-bar-offset, 53px) - 48px);
  z-index: 10051;
  background: var(--surface);
  color: var(--ink);
  border: 1px solid var(--border);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.wa-viewer-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px 10px 16px;
  border-bottom: 1px solid var(--border);
}
.wa-viewer-title {
  flex: 1;
  font-weight: 600;
  overflow-wrap: anywhere;
}
.wa-viewer-img {
  flex: 1;
  min-height: 0;
  object-fit: contain;
  background: var(--surface-2);
}
.wa-viewer-frame {
  flex: 1;
  border: 0;
  background: #fff;
}
</style>
